import { readonly, ref } from "vue";
import { START_LOCATION } from "vue-router";
import type { NavigationFailure, RouteLocationNormalized } from "vue-router";

import { isEditableTarget } from "@/composables/useCommandPalette";

/*****************************************************************************
 * Focus and announce a route change (rank 49, nav-9).
 *
 * A client-side navigation swaps the page without a load, so a screen reader
 * heard nothing and focus stayed on the link just used (or fell to <body>
 * when that link went away): the next Tab started from the old page.
 * #main-content has always been focusable, and Sidebar.vue leaves focus "to
 * the incoming page", but nothing took it.
 *
 * After each page change this moves focus to the new page's <h1> (or <main>
 * when it has none yet) without scrolling, since main-scroll.ts owns that, and
 * puts the page's name in a polite live region (RouteAnnouncer.vue).
 *****************************************************************************/

/** Lets the new page render (route chunks are already resolved) before its heading is looked for. */
export const ROUTE_FOCUS_DELAY_MS = 50;

/**
 * How long the page waits for a modal that is closing on the navigation (the mobile nav drawer slides out for 300ms)
 * before it stops trying to take focus. A modal still open after this owns focus.
 */
export const MODAL_CLOSE_WAIT_MS = 1000;

const MODAL_POLL_MS = 50;

/**
 * The live region is emptied, then refilled after this. Emptied and refilled in the same task, VoiceOver merges the
 * two and does not read identical text again.
 */
export const ANNOUNCE_REFILL_MS = 100;

const MAIN_CONTENT_ID = 'main-content';

const announcement = ref< string >( '' );

/** What RouteAnnouncer's live region reads out: the name of the page just navigated to. */
export const routeAnnouncement = readonly( announcement );

let pendingFocus: ReturnType< typeof setTimeout > | undefined;
let pendingAnnouncement: ReturnType< typeof setTimeout > | undefined;

function collapse( text: string | null | undefined ): string {
    return ( text ?? '' ).replace( /\s+/g, ' ' ).trim();
}

const OPEN_MODAL_SELECTOR = "dialog[open], [role='dialog'][aria-modal='true']";

function isModalOpen(): boolean {
    return !!document.querySelector( OPEN_MODAL_SELECTOR );
}

/*
  Focus that the member is using stays where it is: a text field still on screen (the header search after choosing a
  result, where the next ticker is typed; '/' reopens the palette from anywhere else), or anything inside a dialog.
  A modal open over the page owns focus too (see focusWhenModalCloses).
*/
function keepsFocus( active: Element | null ): boolean {
    if ( !( active instanceof HTMLElement ) || !active.isConnected || active === document.body ) return false;
    return isEditableTarget( active ) || !!active.closest( "dialog, [role='dialog']" );
}

// Focus went nowhere: <body>, or an element that has since left the document.
function focusIsLost(): boolean {
    const active = document.activeElement;
    return !active || active === document.body || !active.isConnected;
}

function focusPage(): void {
    const main = document.getElementById( MAIN_CONTENT_ID );
    if ( !main ) return;

    const target = main.querySelector< HTMLElement >( 'h1' ) ?? main;
    // A heading is not focusable by default; -1 keeps it out of the Tab order.
    if ( !target.hasAttribute( 'tabindex' ) ) {
        target.setAttribute( 'tabindex', '-1' );
    }
    target.focus( { preventScroll: true } );
}

/*
  A modal seen at ROUTE_FOCUS_DELAY_MS may be one that is closing on this navigation: Sidebar.vue's beforeEach closes
  the mobile nav drawer, which stays in the DOM (aria-modal) while it slides out, then takes the focused link with it
  and focus falls to <body>. So wait for it to go, and take focus only if it was lost; a modal that handed focus back
  to its opener keeps that choice, and one that stays open (the routed sector flyout) keeps focus.
*/
function focusWhenModalCloses( waited: number = 0 ): void {
    pendingFocus = setTimeout( () => {
        pendingFocus = undefined;
        if ( isModalOpen() ) {
            if ( waited + MODAL_POLL_MS < MODAL_CLOSE_WAIT_MS ) focusWhenModalCloses( waited + MODAL_POLL_MS );
            return;
        }
        if ( focusIsLost() ) focusPage();
    }, MODAL_POLL_MS );
}

function announce(): void {
    // Cleared first, so a page with the same name as the last one is still read out.
    announcement.value = '';
    clearTimeout( pendingAnnouncement );
    pendingAnnouncement = setTimeout( () => {
        pendingAnnouncement = undefined;
        const main = document.getElementById( MAIN_CONTENT_ID );
        announcement.value = collapse( main?.querySelector( 'h1' )?.textContent ) || collapse( document.title );
    }, ANNOUNCE_REFILL_MS );
}

function focusAndAnnounce(): void {
    if ( !document.getElementById( MAIN_CONTENT_ID ) ) return;

    if ( isModalOpen() ) {
        focusWhenModalCloses();
    } else if ( !keepsFocus( document.activeElement ) ) {
        focusPage();
    }

    announce();
}

/**
 * `router.afterEach` hook. Skips a failed or cancelled navigation, the first load (the browser has just announced the
 * page itself, and the skip link is the first Tab stop) and a query- or hash-only change (a filter, a flyout, the
 * carousel's ?webinar=), which keeps the page on screen.
 */
export function focusRouteChange( to: RouteLocationNormalized, from: RouteLocationNormalized, failure?: NavigationFailure | void ): void {
    if ( failure || from === START_LOCATION || to.path === from.path ) {
        return;
    }

    // Only the latest navigation's page is focused.
    clearTimeout( pendingFocus );
    pendingFocus = setTimeout( () => {
        pendingFocus = undefined;
        focusAndAnnounce();
    }, ROUTE_FOCUS_DELAY_MS );
}

/** Test helper: drop a pending focus or announcement and clear the announcement. */
export function resetRouteFocusState(): void {
    clearTimeout( pendingFocus );
    pendingFocus = undefined;
    clearTimeout( pendingAnnouncement );
    pendingAnnouncement = undefined;
    announcement.value = '';
}
